import React from 'react';
import {
  Grid,
  Tabs,
  Tab,
  Drawer,
  FormControlLabel,
  Switch,
  Button
} from '@mui/material';
import '../Styles/Pages/LaunchPage.css';

// Components
import LaunchFormFields from '../Components/Launch/components/LaunchFormFields';
import LaunchTabContent from '../Components/Launch/components/LaunchTabContent';
import GameDetailsDrawer from '../Components/Details/GameDetailsDrawer';
import ServerExplorer from '../Components/ServerExplorer/ServerExplorer';

// Hooks
import useLaunchData from '../Components/Launch/hooks/useLaunchData';
import useLaunchForm from '../Components/Launch/hooks/useLaunchForm';
import useLaunchTabs from '../Components/Launch/hooks/useLaunchTabs';
import useLaunchDrawers from '../Components/Launch/hooks/useLaunchDrawers';
import useGameFlexAuth from '../Components/Launch/hooks/useGameFlexAuth';

const LaunchPage = () => {
  // Initialize hooks
  const {
    gamesList,
    marketList,
    lobbyList,
    fetchInitialData,
  } = useLaunchData();

  const {
    selectedServer,
    setSelectedServer,
    siteList,
    hasCredentials,
    handleFieldChange,
    handleGameChange,
    handleMarketChange,
    handleLobbyChange,
    handleProductNameChange,
    handleAccountIdChange,
    handleAuthTokenChange,
    handleReset,
    handleSelectServer
  } = useLaunchForm();

  const {
    tabValue,
    mobileOnly,
    filteredGamesList,
    handleTabChange,
    handleMobileToggle
  } = useLaunchTabs(gamesList);

  const {
    dialog,
    gameDetailsOpen,
    serverExplorerOpen,
    gameData,
    toggleDrawer,
    toggleServerExplorer,
    toggleGameDetailsDrawer
  } = useLaunchDrawers();

  const {
    operatorOptions,
    brandOptions,
    handlePlatformChange,
    handleOperatorChange
  } = useGameFlexAuth(setSelectedServer);

  // Initial data fetch
  React.useEffect(() => {
    fetchInitialData();
  }, []);

  return (
    <div className="form-layout">
      <div className="form-container">
        {/* Mobile/Desktop Toggle for GameFlex */}
        {tabValue === 1 && (
          <FormControlLabel
            control={
              <Switch
                checked={mobileOnly}
                onChange={(e) => handleMobileToggle(e.target.checked, setSelectedServer)}
              />
            }
            label={mobileOnly ? "Mobile" : "Desktop"}
            style={{ marginBottom: '10px' }}
          />
        )}

        {/* Form Fields */}
        <LaunchFormFields
          tabValue={tabValue}
          selectedServer={selectedServer}
          hasCredentials={hasCredentials}
          filteredGamesList={filteredGamesList}
          gamesList={gamesList}
          marketList={marketList}
          siteList={siteList}
          operatorOptions={operatorOptions}
          brandOptions={brandOptions}
          languageList={[{ name: 'English' }, { name: 'Spanish' }, { name: 'French' }]}
          currencyCodeOptions={['USD']}
          productList={[]}
          handleFieldChange={handleFieldChange}
          handleGameChange={handleGameChange}
          handleMarketChange={handleMarketChange}
          handlePlatformChange={handlePlatformChange}
          handleOperatorChange={handleOperatorChange}
          handleLobbyChange={handleLobbyChange}
          handleProductNameChange={handleProductNameChange}
          handleAccountIdChange={handleAccountIdChange}
          handleAuthTokenChange={handleAuthTokenChange}
          toggleServerExplorer={toggleServerExplorer}
          setGameDetailsOpen={toggleGameDetailsDrawer}
          handleReset={handleReset}
          lobbyList={lobbyList}
        />
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleReset}
          style={{ marginTop: '10px' }}
        >
          Reset Fields
        </Button>
        {/* Tabs Section */}
        <Grid container spacing={2} style={{ marginTop: "20px" }}>
          <Grid item xs={12}>
            <Tabs value={tabValue} onChange={handleTabChange} className="tabs-item">
              <Tab label="Redirector" />
              <Tab label="GameFlex" />
              <Tab label="Pathfinder" />
              <Tab label="Universal Game Launcher" />
              <Tab label="Playcheck" />
              <Tab label="Helpfiles" />
            </Tabs>
          </Grid>
        </Grid>

        {/* Tab Content */}
        <LaunchTabContent
          tabValue={tabValue}
          selectedServer={selectedServer}
          showFrame={toggleDrawer}
        />
      </div>

      {/* Game Details Drawer */}
      <Drawer
        anchor="right"
        open={gameDetailsOpen}
        onClose={() => toggleGameDetailsDrawer(false)}
        PaperProps={{
          className: "drawer-paper",
          sx: {
            width: '500px',
            maxWidth: '500px',
            height: '100%',
            backgroundColor: '#f5f5f5',
            boxShadow: 'none'
          }
        }}
      >
        <GameDetailsDrawer
          game={gameData}
          selectedGame={selectedServer}
          open={gameDetailsOpen}
          singleGameData={true}
          onClose={() => toggleGameDetailsDrawer(false)}
        />
      </Drawer>

      {/* Server Explorer Drawer */}
      <Drawer
        anchor="right"
        open={serverExplorerOpen}
        onClose={() => toggleServerExplorer(false)}
        PaperProps={{ style: { width: '50%' } }}
      >
        <div style={{ padding: '20px' }}>
          <ServerExplorer
            onClose={() => toggleServerExplorer(false)}
            onSelectServer={(serverData) => {
              handleSelectServer(serverData);
              toggleServerExplorer(false);  // Close drawer after selection
            }}
          />
        </div>
      </Drawer>
    </div>
  );
};

export default LaunchPage;